import React, { useCallback, useEffect, useRef, useState } from "react";
import "../styles/whatsapp-alerts.css";

const API_BASE = window.location.hostname === "localhost" ? "http://localhost:5000" : "";
const LABELS = { ready: "Connected", connecting: "Connecting", syncing: "Synchronizing", qr: "Scan QR", disconnected: "Disconnected", error: "Connection error" };

export default function WhatsAppAlerts() {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const mounted = useRef(true);

  const request = useCallback(async (action = "status") => {
    const response = await fetch(`${API_BASE}/api/whatsapp-alerts/${action}`, {
      method: action === "status" ? "GET" : "POST", cache: "no-store",
      headers: { Authorization: `Bearer ${localStorage.getItem("ct_login_token") || ""}` },
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || "Unable to reach WhatsApp controls");
    return data;
  }, []);

  useEffect(() => {
    mounted.current = true;
    let timer;
    const poll = async () => {
      let delay = 5000;
      try {
        const next = await request();
        if (mounted.current) { setStatus(next); setError(""); }
        if (next.connection === "ready") delay = 15000;
      } catch (failure) { if (mounted.current) setError(failure.message); }
      if (mounted.current) timer = setTimeout(poll, delay);
    };
    poll();
    return () => { mounted.current = false; clearTimeout(timer); };
  }, [request]);

  const action = async name => {
    setBusy(true); setError("");
    try { const next = await request(name); if (mounted.current) setStatus(next); }
    catch (failure) { if (mounted.current) setError(failure.message); }
    finally { if (mounted.current) setBusy(false); }
  };
  const connected = status?.connection === "ready";
  return (
    <main className="wa-alerts">
      <header><span className="wa-eyebrow">NEXOVATE GROUPS</span><h1>WhatsApp Alerts</h1><p>Connect once. Control your market alerts here.</p></header>
      {error && <div className="wa-error" role="alert">{error}</div>}
      <div className="wa-grid">
        <section className="wa-panel">
          <h2>WhatsApp connection</h2>
          <div className={`wa-pill ${connected ? "wa-on" : ""}`}>{LABELS[status?.connection] || "Checking connection…"}</div>
          <p>{connected ? "Your saved WhatsApp login is active. No QR scan is needed." : status?.detail || "Checking your saved login…"}</p>
          <p className="wa-note">Refreshing or closing Nexovate keeps WhatsApp connected on the server. Unlink this device in WhatsApp → Linked devices to log out. WhatsApp may also expire the session.</p>
          {status?.qr && <div className="wa-qr"><img src={status.qr} width="280" height="280" alt="WhatsApp linked-device login QR code" /><p>On your phone: WhatsApp → Linked devices → Link a device.</p></div>}
          {status && !connected && !status.qr && <div className="wa-buttons">
            <button disabled={busy || ["connecting", "syncing"].includes(status.connection)} onClick={() => action("connect")}>Connect WhatsApp</button>
            <button className="wa-secondary" disabled={busy} onClick={() => action("reconnect")}>Reconnect saved login</button>
          </div>}
          {status?.error && <p className="wa-warning">{status.error}</p>}
        </section>
        <section className="wa-panel">
          <h2>Message flow</h2><p className="wa-destination">{status?.group || "Nexo Alerts"}</p>
          <div className={`wa-pill ${status?.enabled ? "wa-on" : ""}`}>{status?.enabled ? "Enabled" : "Stopped"}</div>
          <p>{status?.enabled ? connected ? "Alerts will send when their market conditions and schedule are met." : "Flow is enabled and will resume when WhatsApp reconnects." : "Start to enable alerts. Stopping keeps your WhatsApp login saved."}</p>
          <div className="wa-buttons">
            <button disabled={busy || !connected || status?.enabled} onClick={() => action("start")}>Start alerts</button>
            <button className="wa-stop" disabled={busy || !status?.enabled} onClick={() => action("stop")}>Stop alerts</button>
          </div>
          <p className="wa-note">Your selection is remembered after a server restart. You can close this page while alerts run.</p>
        </section>
      </div>
      <section className="wa-panel wa-schedule"><h2>Delivery schedule · IST</h2>
        <div><strong>Monday / Tuesday / Friday</strong><span>NIFTY · Market Reaction + NIFTY ITM</span></div>
        <div><strong>Wednesday / Thursday</strong><span>SENSEX · Market Reaction + SENSEX ITM</span></div>
        <p>09:15–15:45 · One alert per qualifying minute when CE or PE volume changes by ±10,00,000 or more. ITM snapshots every 15 minutes. No weekend sends.</p>
      </section>
    </main>
  );
}
